import { useState, type FC } from "react";

import { CASE_INSIGHTS_ANCHOR } from "@/components/settings/CaseInsightsSection";
import { SettingsModal } from "@/components/settings/SettingsModal";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import type { KeyFactActions } from "../hooks/useKeyFactActions";
import type { KeyFact, KeyFactAutomation, KeyFactsCase } from "../types";
import { Group } from "./Group";

/** Settings > Case Preparation, scrolled to the Case Insights defaults. */
export const CASE_INSIGHTS_SETTINGS = `case-preparation#${CASE_INSIGHTS_ANCHOR}`;

/** One entry per key fact type on the case (multi-instance ones once). */
const kinds = (facts: readonly KeyFact[]): KeyFact[] => {
  const seen = new Map<string, KeyFact>();
  for (const fact of facts) if (!seen.has(fact.kind)) seen.set(fact.kind, fact);
  return [...seen.values()];
};

/** What the firm's switches do on every case, in a sentence. */
export const firmDefaultsText = (automation: KeyFactAutomation): string => {
  const current = automation.keep_current
    ? "keeps insights current as the case changes"
    : "updates insights only when you recheck";
  const memos = automation.run_analyses
    ? "writes research memos on its own"
    : "writes research memos only when asked";
  return `Your firm ${current} and ${memos}.`;
};

/**
 * The case's own switches: each key fact type on or off, and, where memos
 * are written on their own or one was turned off here, that fact's memo.
 * The firm's switches live in Settings > Case Preparation.
 */
export const OnThisCase: FC<{
  data: KeyFactsCase;
  actions: KeyFactActions;
  onSaved: () => void;
}> = ({ data, actions, onSaved }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { automation, disabled_keys, disabled_analyses } = data.settings;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-fg-3" data-testid="firm-defaults-pointer">
        {firmDefaultsText(automation)} Firm defaults:{" "}
        <Button
          variant="link"
          size="xs"
          className="text-sm"
          onClick={() => setSettingsOpen(true)}
        >
          Settings &gt; Case Preparation
        </Button>
      </p>
      <Group title="On This Case">
        {kinds(data.facts).map((fact) => {
          const on = !disabled_keys.includes(fact.kind);
          const memoOff = disabled_analyses.includes(fact.kind);
          const memoSwitch =
            fact.has_analysis && (automation.run_analyses || memoOff);
          const memoId = `kf-memo-${fact.kind}-description`;
          return (
            <li key={fact.kind} className="flex flex-col gap-2 px-4 py-3">
              <div className="flex items-center gap-4">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {fact.label}
                </span>
                <Switch
                  checked={on}
                  disabled={actions.busy !== null}
                  onCheckedChange={(v) =>
                    void actions
                      .act(fact.key, { action: v ? "enable" : "disable" })
                      .then(onSaved)
                  }
                  aria-label={fact.label}
                />
              </div>
              {memoSwitch ? (
                <div
                  className={cn(
                    "ml-1 flex items-center gap-4 border-l border-line-2 pl-3",
                    !on && "opacity-50"
                  )}
                >
                  <span
                    id={memoId}
                    className="min-w-0 flex-1 text-sm text-fg-3"
                  >
                    {memoOff
                      ? "Its research memo is off on this case, even when asked for."
                      : "Write its research memo on this case. It appears under Research."}
                  </span>
                  <Switch
                    aria-describedby={memoId}
                    checked={on && !memoOff}
                    disabled={!on || actions.busy !== null}
                    onCheckedChange={(v) =>
                      void actions
                        .act(fact.key, {
                          action: v ? "enable" : "disable",
                          scope: "analysis"
                        })
                        .then(onSaved)
                    }
                    aria-label={`${fact.label} research memo`}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </Group>
      {settingsOpen ? (
        <SettingsModal
          open
          onOpenChange={setSettingsOpen}
          initialSection={CASE_INSIGHTS_SETTINGS}
        />
      ) : null}
    </div>
  );
};
